/**
 * A fixed-capacity, allocation-light particle pool for clear-effect bursts.
 * Owned by `EffectsController` (never the renderer — the same split
 * `docs/decisions/0006` draws between timer-driven effect *state* and draw
 * calls): `spawn` writes into a pre-allocated ring of slots, reusing the
 * next slot in ring order once the pool is at capacity, so a storm of rapid
 * clears costs no more per frame than one already full — capped on low end
 * by construction, not by a frame-rate check, and nothing here allocates a
 * new object per particle per frame.
 *
 * Positions are in *board cell units* (column, row), not pixels, using the
 * same "row index at the instant of the clear" convention
 * `EffectsController`'s existing `clearingRows` already relies on — the
 * renderer converts with its own current `cellPx` at paint time.
 */
import type { ParticleFade, ParticleShape } from './theme-effects.js';

export interface ParticleSpawnOptions {
  readonly x: number;
  readonly y: number;
  readonly vx: number;
  readonly vy: number;
  readonly color: string;
  readonly shape: ParticleShape;
  readonly glow: boolean;
  readonly maxBlurPx: number;
  readonly fade: ParticleFade;
  /** Cell-units/second². */
  readonly gravity: number;
  readonly lifeMs: number;
  /** Fraction of a cell — the particle's own size, before end-of-life shrink. */
  readonly sizeCells: number;
}

export interface ParticleSnapshot {
  readonly x: number;
  readonly y: number;
  readonly color: string;
  readonly shape: ParticleShape;
  readonly glow: boolean;
  readonly maxBlurPx: number;
  readonly alpha: number;
  readonly sizeCells: number;
}

interface Slot {
  alive: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  gravity: number;
  ageMs: number;
  lifeMs: number;
  color: string;
  shape: ParticleShape;
  glow: boolean;
  maxBlurPx: number;
  fade: ParticleFade;
  sizeCells: number;
}

/** Fade snaps to invisible once life is 82% spent, rather than ramping down — `concrete`'s "cuts instead of fades." */
const CUT_FADE_THRESHOLD = 0.82;

function makeSlot(): Slot {
  return {
    alive: false,
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    gravity: 0,
    ageMs: 0,
    lifeMs: 1,
    color: '#ffffff',
    shape: 'spark',
    glow: false,
    maxBlurPx: 0,
    fade: 'linear',
    sizeCells: 0.2,
  };
}

export class ParticlePool {
  private readonly slots: Slot[];
  private cursor = 0;

  constructor(readonly capacity = 240) {
    this.slots = Array.from({ length: capacity }, makeSlot);
  }

  get activeCount(): number {
    let n = 0;
    for (const slot of this.slots) if (slot.alive) n++;
    return n;
  }

  /**
   * Write one particle into the next slot in ring order — dead or not. A
   * sustained storm of clears therefore degrades gracefully: the oldest
   * particles are recycled first, so the pool's memory footprint never
   * grows past `capacity` regardless of how many `spawn` calls arrive.
   */
  spawn(opts: ParticleSpawnOptions): void {
    const slot = this.slots[this.cursor];
    this.cursor = (this.cursor + 1) % this.capacity;
    if (slot === undefined) return;
    slot.alive = true;
    slot.x = opts.x;
    slot.y = opts.y;
    slot.vx = opts.vx;
    slot.vy = opts.vy;
    slot.gravity = opts.gravity;
    slot.ageMs = 0;
    slot.lifeMs = Math.max(1, opts.lifeMs);
    slot.color = opts.color;
    slot.shape = opts.shape;
    slot.glow = opts.glow;
    slot.maxBlurPx = opts.maxBlurPx;
    slot.fade = opts.fade;
    slot.sizeCells = opts.sizeCells;
  }

  /** Advance every live particle by `dtMs`, retiring any that have finished their life. No allocation. */
  advance(dtMs: number): void {
    const dtSec = dtMs / 1000;
    for (const slot of this.slots) {
      if (!slot.alive) continue;
      slot.ageMs += dtMs;
      if (slot.ageMs >= slot.lifeMs) {
        slot.alive = false;
        continue;
      }
      slot.vy += slot.gravity * dtSec;
      slot.x += slot.vx * dtSec;
      slot.y += slot.vy * dtSec;
    }
  }

  /** A read-only snapshot of every live particle, for this frame's render call. */
  snapshot(): ParticleSnapshot[] {
    const out: ParticleSnapshot[] = [];
    for (const slot of this.slots) {
      if (!slot.alive) continue;
      const t = slot.ageMs / slot.lifeMs;
      const alpha = slot.fade === 'cut' ? (t < CUT_FADE_THRESHOLD ? 1 : 0) : Math.max(0, 1 - t);
      out.push({
        x: slot.x,
        y: slot.y,
        color: slot.color,
        shape: slot.shape,
        glow: slot.glow,
        maxBlurPx: slot.maxBlurPx,
        alpha,
        sizeCells: slot.sizeCells * (1 - t * 0.3),
      });
    }
    return out;
  }

  /** Retire every particle immediately — a fresh run should not inherit the previous one's tail. */
  clear(): void {
    for (const slot of this.slots) slot.alive = false;
  }
}
